import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { Card, CardTitle, Insight, Btn } from '../components/UI';
import { supabase } from '../supabaseClient';
import EditLocationModal from '../components/EditLocationModal';
import { getDisplayAddress } from '../utils/location';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
function fmt(n) { return n === null ? '…' : Number(n).toLocaleString(); }

const LOCATION_TYPES = [
  '','Clinic','Hospital','Pharmacy','Dental','Optical','Wellness','Gym','Spa',
  'Mental Health','Physiotherapy','Laboratory','Urgent Care','Telehealth','Other',
];

const BULK_FIELDS = [
  { value: 'type',          label: 'Type' },
  { value: 'country',       label: 'Country' },
  { value: 'city',          label: 'City' },
  { value: 'last_verified', label: 'Last verified date' },
  { value: 'tags',          label: 'Tags' },
  { value: 'notes',         label: 'Notes' },
];

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setMob(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

function LocationEditorBody() {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [query,      setQuery]     = useState('');
  const [country,    setCountry]   = useState('');
  const [typeF,      setTypeF]     = useState('');
  const [results,    setResults]   = useState([]);
  const [total,      setTotal]     = useState(0);
  const [page,       setPage]      = useState(0);
  const [loading,    setLoading]   = useState(false);
  const [searched,   setSearched]  = useState(false);
  const [countries,  setCountries] = useState([]);
  const [editing,    setEditing]   = useState(null);
  const [toast,      setToast]     = useState(null);
  // Bulk selection
  const [selected,   setSelected]  = useState(new Set());
  const [bulkField,  setBulkField] = useState('last_verified');
  const [bulkValue,  setBulkValue] = useState('');
  const [bulkWorking,setBulkWork]  = useState(false);
  const [showBulk,   setShowBulk]  = useState(false);
  const [confirmDel, setConfDel]   = useState(false);
  const PAGE_SIZE = 20;
  const isMobile  = useIsMobile();

  function showToast(msg, type) {
    setToast({ msg, type: type || 'ok' });
    setTimeout(() => setToast(null), 3500);
  }

  useEffect(() => {
    supabase.rpc('get_distinct_countries')
      .then(({ data }) => {
        if (data) setCountries(data.map(r => r.country || r).filter(Boolean).sort());
      })
      .catch(() => {
        supabase.from('Locations').select('country').limit(3000)
          .then(({ data }) => {
            const unique = [...new Set((data || []).map(r => r.country).filter(Boolean))].sort();
            setCountries(unique);
          });
      });
  }, []);

  const doSearch = useCallback(async (pg = 0) => {
    setLoading(true);
    setSearched(true);
    setSelected(new Set());
    try {
      const { data, error } = await supabase.rpc('admin_search_locations', {
        p_query:   query.trim()   || null,
        p_country: country.trim() || null,
        p_type:    typeF.trim()   || null,
        p_limit:   PAGE_SIZE,
        p_offset:  pg * PAGE_SIZE,
      });
      if (error) throw error;
      setResults(data || []);
      setTotal(data?.[0]?.total_count ? parseInt(data[0].total_count) : (data || []).length);
      setPage(pg);
    } catch (_e) { setResults([]); }
    setLoading(false);
  }, [query, country, typeF]);

  function handleKey(e) { if (e.key === 'Enter') doSearch(0); }

  // ── Selection helpers ──────────────────────────────────────
  function toggleOne(id) {
    setSelected(s => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleAll() {
    if (selected.size === results.length) setSelected(new Set());
    else setSelected(new Set(results.map(r => String(r.id))));
  }

  // ── Bulk delete ───────────────────────────────────────────
  async function handleBulkDelete() {
    setBulkWork(true);
    const ids = [...selected];
    try {
      const { data, error } = await supabase.rpc('admin_bulk_delete_locations', { p_ids: ids });
      if (error) throw error;
      const deleted = data || ids.length;
      setResults(r => r.filter(x => !selected.has(String(x.id))));
      setTotal(t => t - deleted);
      setSelected(new Set());
      setConfDel(false);
      showToast(`Deleted ${fmt(deleted)} records`, 'warn');
      try {
        await supabase.rpc('admin_log_action', {
          p_actor: 'admin', p_actor_email: '',
          p_action_type: 'bulk_delete',
          p_subject: `${deleted} locations`,
          p_detail: 'Bulk delete via Location Editor',
        });
      } catch (_e) {}
    } catch (err) { showToast('Delete failed: ' + err.message, 'warn'); }
    setBulkWork(false);
  }

  // ── Bulk field update ─────────────────────────────────────
  async function handleBulkUpdate() {
    if (!bulkValue.trim()) return;
    setBulkWork(true);
    const ids = [...selected];
    try {
      const { data, error } = await supabase.rpc('admin_bulk_update_locations', {
        p_ids:   ids,
        p_field: bulkField,
        p_value: bulkValue.trim(),
      });
      if (error) throw error;
      const updated = data || ids.length;
      // Reflect in table
      setResults(r => r.map(x =>
        selected.has(String(x.id)) ? { ...x, [bulkField]: bulkValue.trim() } : x
      ));
      setSelected(new Set());
      setShowBulk(false);
      setBulkValue('');
      showToast(`Updated ${fmt(updated)} records — ${BULK_FIELDS.find(f=>f.value===bulkField)?.label} set to "${bulkValue.trim()}"`);
      try {
        await supabase.rpc('admin_log_action', {
          p_actor: 'admin', p_actor_email: '',
          p_action_type: 'bulk_update',
          p_subject: `${updated} locations`,
          p_detail: `Set ${bulkField} = ${bulkValue.trim()}`,
        });
      } catch (_e) {}
    } catch (err) { showToast('Update failed: ' + err.message, 'warn'); }
    setBulkWork(false);
  }

  // ── Export selected as CSV ────────────────────────────────
  function exportSelected() {
    const rows = selected.size > 0 ? results.filter(r => selected.has(String(r.id))) : results;
    const header = 'id,name,address,country,city,type,phone,website,booking_url,specialties_url,latitude,longitude,tags,last_verified\n';
    const csv = rows.map(r =>
      [r.id,r.name,r.address,r.country,r.city,r.type,r.phone,r.website,r.booking_url,r.specialties_url,r.latitude,r.longitude,r.tags,r.last_verified]
        .map(v => v === null ? '' : '"' + String(v).replace(/"/g, '""') + '"').join(',')
    ).join('\n');
    const blob = new Blob([header + csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `sanoire-locations-${new Date().toISOString().split('T')[0]}.csv`; a.click();
    URL.revokeObjectURL(url);
    showToast(`Exported ${fmt(rows.length)} records`);
  }

  function handleSaved(updated) {
    if (updated === null) {
      setResults(r => r.filter(x => String(x.id) !== String(editing?.id)));
      setTotal(t => t - 1);
      showToast('Record deleted', 'warn');
      setEditing(null);
    } else {
      setResults(r => r.map(x => String(x.id) === String(updated.id) ? { ...x, ...updated } : x));
      showToast('Record saved');
    }
  }

  const totalPages  = Math.ceil(total / PAGE_SIZE);
  const allSelected = results.length > 0 && selected.size === results.length;
  const someSelected = selected.size > 0;

  const Dot = ({ val }) => (
    <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: val ? '#2da06a' : '#e0e8ef' }} />
  );

  const sel = (label, val, onChange, opts, style) => (
    <div style={style}>
      <div style={{ fontSize: '.63rem', color: '#6F8FA7', marginBottom: 3 }}>{label}</div>
      <select value={val} onChange={e => onChange(e.target.value)} onKeyDown={handleKey}
        style={{ fontSize: '.72rem', border: '1px solid #d8eaf4', borderRadius: 7, padding: '8px 10px', color: '#1a3040', fontFamily: F, width: '100%' }}>
        {opts}
      </select>
    </div>
  );

  return (
    <div style={{ fontFamily: F }}>
      {toast && (
        <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', background: toast.type === 'warn' ? '#7c3a00' : '#1b5e20', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: '.78rem', fontWeight: 500, zIndex: 7000, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>{toast.msg}</div>
      )}
      {editing && (
        <EditLocationModal record={editing} onClose={() => setEditing(null)} onSaved={handleSaved} />
      )}

      {/* ── Search bar ── */}
      <Card>
        <CardTitle>Search locations</CardTitle>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr auto', gap: 8, alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: '.63rem', color: '#6F8FA7', marginBottom: 3 }}>Name or address</div>
            <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKey}
              placeholder="Search by name, address…"
              style={{ fontSize: '.74rem', border: '1px solid #d8eaf4', borderRadius: 7, padding: '8px 10px', color: '#1a3040', outline: 'none', fontFamily: F, width: '100%', boxSizing: 'border-box' }} />
          </div>
          {sel('Country', country, setCountry,
            [<option key="" value="">All countries</option>, ...countries.map(c => <option key={c} value={c}>{c}</option>)],
            { marginTop: isMobile ? 8 : 0 }
          )}
          {sel('Type', typeF, setTypeF,
            LOCATION_TYPES.map(t => <option key={t} value={t}>{t || 'All types'}</option>),
            { marginTop: isMobile ? 8 : 0 }
          )}
          <div style={{ marginTop: isMobile ? 10 : 0 }}>
            <Btn onClick={() => doSearch(0)} disabled={loading}>{loading ? 'Searching…' : 'Search'}</Btn>
          </div>
        </div>
        <div style={{ marginTop: 8, fontSize: '.63rem', color: '#9eb8c8' }}>
          Leave all fields empty and search to browse. Press Enter to search.
        </div>
      </Card>

      {/* ── Bulk action bar (appears when rows selected) ── */}
      {someSelected && (
        <div style={{ background: '#0f1e28', borderRadius: 10, padding: '10px 16px', marginBottom: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '.75rem', color: '#AFE1F6', fontWeight: 600 }}>{selected.size} selected</span>
          <div style={{ flex: 1 }} />

          {/* Bulk update */}
          <button onClick={() => setShowBulk(b => !b)}
            style={{ padding: '6px 14px', background: 'rgba(175,225,246,0.12)', color: '#AFE1F6', border: '1px solid rgba(175,225,246,0.25)', borderRadius: 7, fontSize: '.7rem', fontWeight: 500, cursor: 'pointer', fontFamily: F }}>
            {showBulk ? 'Cancel' : 'Bulk edit field'}
          </button>

          {/* Export */}
          <button onClick={exportSelected}
            style={{ padding: '6px 14px', background: 'rgba(175,225,246,0.12)', color: '#AFE1F6', border: '1px solid rgba(175,225,246,0.25)', borderRadius: 7, fontSize: '.7rem', cursor: 'pointer', fontFamily: F }}>
            Export CSV
          </button>

          {/* Bulk delete */}
          {confirmDel ? (
            <>
              <span style={{ fontSize: '.7rem', color: '#f87171' }}>Delete {selected.size} records?</span>
              <button onClick={handleBulkDelete} disabled={bulkWorking}
                style={{ padding: '6px 14px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 7, fontSize: '.7rem', fontWeight: 600, cursor: 'pointer', fontFamily: F }}>
                {bulkWorking ? 'Deleting…' : 'Confirm delete'}
              </button>
              <button onClick={() => setConfDel(false)}
                style={{ padding: '6px 12px', background: 'transparent', color: '#AFE1F6', border: '1px solid rgba(175,225,246,0.2)', borderRadius: 7, fontSize: '.7rem', cursor: 'pointer', fontFamily: F }}>
                Cancel
              </button>
            </>
          ) : (
            <button onClick={() => setConfDel(true)}
              style={{ padding: '6px 14px', background: 'rgba(224,92,92,0.15)', color: '#f87171', border: '1px solid rgba(224,92,92,0.3)', borderRadius: 7, fontSize: '.7rem', cursor: 'pointer', fontFamily: F }}>
              Delete selected
            </button>
          )}

          {/* Inline bulk edit panel */}
          {showBulk && (
            <div style={{ width: '100%', background: 'rgba(175,225,246,0.06)', borderRadius: 8, padding: '12px 14px', marginTop: 4, display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 8, alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontSize: '.6rem', color: '#6F8FA7', marginBottom: 3 }}>Field to update</div>
                <select value={bulkField} onChange={e => setBulkField(e.target.value)}
                  style={{ fontSize: '.72rem', border: '1px solid rgba(175,225,246,0.2)', borderRadius: 6, padding: '6px 9px', color: '#e8f4fb', background: 'rgba(15,30,40,0.6)', fontFamily: F, width: '100%' }}>
                  {BULK_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize: '.6rem', color: '#6F8FA7', marginBottom: 3 }}>
                  Set value for all {selected.size} selected records
                </div>
                {bulkField === 'type' ? (
                  <select value={bulkValue} onChange={e => setBulkValue(e.target.value)}
                    style={{ fontSize: '.72rem', border: '1px solid rgba(175,225,246,0.2)', borderRadius: 6, padding: '6px 9px', color: '#e8f4fb', background: 'rgba(15,30,40,0.6)', fontFamily: F, width: '100%' }}>
                    <option value="">— select type —</option>
                    {LOCATION_TYPES.filter(t=>t).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                ) : bulkField === 'last_verified' ? (
                  <input type="date" value={bulkValue} onChange={e => setBulkValue(e.target.value)}
                    style={{ fontSize: '.72rem', border: '1px solid rgba(175,225,246,0.2)', borderRadius: 6, padding: '6px 9px', color: '#e8f4fb', background: 'rgba(15,30,40,0.6)', fontFamily: F, width: '100%', outline: 'none' }} />
                ) : (
                  <input type="text" value={bulkValue} onChange={e => setBulkValue(e.target.value)}
                    placeholder={`New ${BULK_FIELDS.find(f=>f.value===bulkField)?.label || 'value'}…`}
                    style={{ fontSize: '.72rem', border: '1px solid rgba(175,225,246,0.2)', borderRadius: 6, padding: '6px 9px', color: '#e8f4fb', background: 'rgba(15,30,40,0.6)', fontFamily: F, width: '100%', outline: 'none', boxSizing: 'border-box' }} />
                )}
              </div>
              <button onClick={handleBulkUpdate} disabled={bulkWorking || !bulkValue.trim()}
                style={{ padding: '7px 16px', background: bulkValue.trim() ? '#2da06a' : '#4a6272', color: '#fff', border: 'none', borderRadius: 7, fontSize: '.72rem', fontWeight: 600, cursor: bulkValue.trim() ? 'pointer' : 'default', fontFamily: F }}>
                {bulkWorking ? 'Updating…' : 'Apply to ' + selected.size}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Results ── */}
      {searched && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <CardTitle>
              {loading ? 'Searching…' : results.length === 0 ? 'No results' : `${fmt(total)} result${total !== 1 ? 's' : ''}`}
            </CardTitle>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {!loading && results.length > 0 && (
                <button onClick={exportSelected}
                  style={{ fontSize: '.65rem', color: '#6F8FA7', background: 'none', border: 'none', cursor: 'pointer', padding: '3px 8px', textDecoration: 'underline', fontFamily: F }}>
                  {someSelected ? `Export ${selected.size} selected` : 'Export page CSV'}
                </button>
              )}
              {results.length > 0 && (
                <span style={{ fontSize: '.6rem', color: '#9eb8c8' }}>
                  <Dot val={true} /> has · <Dot val={false} /> missing
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <div style={{ color: '#9eb8c8', fontSize: '.75rem', padding: '16px 0' }}>Loading…</div>
          ) : results.length === 0 ? (
            <Insight type="info">No locations match your search. Try broader terms or clear the filters.</Insight>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: 640 }}>
                  {/* Header */}
                  <div style={{ display: 'grid', gridTemplateColumns: '24px 2fr 1fr .8fr .8fr .3fr .3fr .3fr .3fr .3fr 1fr', gap: 6, paddingBottom: 7, borderBottom: '1px solid #edf6fb', marginBottom: 4, alignItems: 'center' }}>
                    <input type="checkbox" checked={allSelected} onChange={toggleAll}
                      style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#314F5C' }} />
                    {['Name','Country','Type','Address','Ph','Web','Bk','Sp','GPS','Action'].map(h => (
                      <span key={h} style={{ fontSize: '.55rem', color: '#9eb8c8', textTransform: 'uppercase', letterSpacing: '.06em' }}>{h}</span>
                    ))}
                  </div>

                  {results.map(r => {
                    const isSelected = selected.has(String(r.id));
                    const isPremium  = r.phone && r.website && r.booking_url && r.specialties_url;
                    return (
                      <div key={r.id} onClick={() => toggleOne(String(r.id))}
                        style={{ display: 'grid', gridTemplateColumns: '24px 2fr 1fr .8fr .8fr .3fr .3fr .3fr .3fr .3fr 1fr', gap: 6, padding: '7px 0', borderBottom: '.5px solid rgba(49,79,92,.06)', alignItems: 'center', background: isSelected ? 'rgba(49,79,92,0.05)' : 'transparent', cursor: 'pointer', borderRadius: isSelected ? 4 : 0 }}>
                        <input type="checkbox" checked={isSelected} onChange={() => toggleOne(String(r.id))}
                          onClick={e => e.stopPropagation()}
                          style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#314F5C' }} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '.72rem', color: '#1a3040', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {isPremium && <span style={{ fontSize: '.52rem', color: '#9F7E4F', fontWeight: 700, marginRight: 4 }}>★</span>}
                            {r.name || <em style={{ color: '#e05c5c' }}>no name</em>}
                          </div>
                          <div style={{ fontSize: '.6rem', color: '#9eb8c8', fontFamily: 'monospace' }}>{r.id}</div>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '.69rem', color: '#314F5C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.country || '—'}</div>
                          <div style={{ fontSize: '.62rem', color: '#9eb8c8' }}>{r.city || ''}</div>
                        </div>
                        <span style={{ fontSize: '.66rem', color: '#6F8FA7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.type || '—'}</span>
                        <span style={{ fontSize: '.63rem', color: '#9eb8c8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={getDisplayAddress(r)}>{getDisplayAddress(r) || <em style={{ color: '#e05c5c' }}>missing</em>}</span>
                        <Dot val={r.phone} />
                        <Dot val={r.website} />
                        <Dot val={r.booking_url} />
                        <Dot val={r.specialties_url} />
                        <Dot val={r.latitude && r.longitude} />
                        <div onClick={e => e.stopPropagation()}>
                          <button onClick={() => setEditing(r)}
                            style={{ padding: '3px 10px', background: t.accent, color: '#fff', border: 'none', borderRadius: 5, fontSize: '.65rem', fontWeight: 600, cursor: 'pointer', fontFamily: F }}>
                            Edit
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14, justifyContent: 'flex-end' }}>
                  <span style={{ fontSize: '.65rem', color: '#9eb8c8' }}>Page {page + 1} of {totalPages} · {fmt(total)} total</span>
                  <Btn small variant="outline" onClick={() => doSearch(page - 1)} disabled={page === 0 || loading}>← Prev</Btn>
                  <Btn small variant="outline" onClick={() => doSearch(page + 1)} disabled={page + 1 >= totalPages || loading}>Next →</Btn>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* ── Legend (pre-search state) ── */}
      {!searched && (
        <Card>
          <CardTitle>Column legend & bulk actions</CardTitle>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
            {[
              { label: 'Ph — Phone', desc: 'Direct phone number. Required for premium.' },
              { label: 'Web — Website', desc: 'Main website URL. Required for premium.' },
              { label: 'Bk — Booking URL', desc: 'Online booking link. Required for premium.' },
              { label: 'Sp — Specialties URL', desc: 'Specialties/services page. Required for premium.' },
              { label: 'GPS — Coordinates', desc: 'lat/lng needed for map & "near me" search.' },
              { label: '★ — Premium', desc: 'Has all 4: phone + website + booking + specialties.' },
            ].map(({ label, desc }) => (
              <div key={label}>
                <div style={{ fontSize: '.72rem', fontWeight: 600, color: '#314F5C' }}>{label}</div>
                <div style={{ fontSize: '.65rem', color: '#9eb8c8', marginTop: 1 }}>{desc}</div>
              </div>
            ))}
          </div>
          <div style={{ borderTop: '1px solid #edf6fb', paddingTop: 14 }}>
            <div style={{ fontSize: '.72rem', fontWeight: 600, color: '#314F5C', marginBottom: 6 }}>Bulk actions (available after selecting rows)</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 10 }}>
              {[
                { label: 'Bulk edit field', desc: 'Update type, country, city, last verified date, tags, or notes across all selected rows at once.' },
                { label: 'Export CSV', desc: 'Download selected rows (or the whole page) as a spreadsheet-ready CSV file.' },
                { label: 'Bulk delete', desc: 'Permanently remove all selected records. Requires confirmation. Logged to Activity Log.' },
              ].map(({ label, desc }) => (
                <div key={label} style={{ background: '#f5fafc', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: '.72rem', fontWeight: 600, color: '#314F5C', marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: '.65rem', color: '#9eb8c8', lineHeight: 1.5 }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

export default function LocationEditor(props) {
  if (props && props.embed) return <LocationEditorBody {...props} />;
  return <ConsoleShell title="Location Editor"><LocationEditorBody {...props} /></ConsoleShell>;
}
